/**
 * Magic-login redemption page — reached from Kajabi welcome emails, partner
 * invites, and future one-click sign-in flows.
 *
 * Reads `?token=xxx` from the URL, POSTs to /api/auth/magic-login, and on
 * success drops the user on their dashboard (already authenticated via the
 * session cookies set by the backend).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('working');  // working | error
  const [errorMsg, setErrorMsg] = useState('');
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return;  // React strict-mode double-mount guard
    attemptedRef.current = true;
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is missing its login token. Try clicking the button in your welcome email again.');
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/auth/magic-login`, { token }, { withCredentials: true });
        // Reload auth context so the cookie-backed session is picked up.
        await checkAuth?.();
        toast.success('Welcome to Shaadi Planner');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const msg = err?.response?.data?.detail || 'This login link is invalid or expired.';
        setErrorMsg(msg);
        setStatus('error');
      }
    })();
  }, [token, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-page px-4" data-testid="magic-login-page">
      <div className="w-full max-w-md text-center">
        <div className="mb-6">
          <span className="font-brand text-3xl text-charcoal">Shaadi Planner</span>
        </div>
        {status === 'working' && (
          <div className="bg-white border border-border rounded-sm p-8">
            <div className="w-8 h-8 border-2 border-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted">Signing you in…</p>
          </div>
        )}
        {status === 'error' && (
          <div className="bg-white border border-border rounded-sm p-8" data-testid="magic-login-error">
            <h1 className="font-serif text-2xl text-charcoal mb-3">Couldn&apos;t sign you in</h1>
            <p className="text-sm text-muted leading-relaxed mb-6">{errorMsg}</p>
            <div className="space-y-2">
              <Link
                to="/login"
                className="btn-primary inline-block w-full"
                data-testid="magic-login-go-to-login-btn"
              >
                Sign in with password
              </Link>
              <p className="text-[11px] text-muted mt-3">
                Use the temporary password from your welcome email — or reset it if
                you&apos;ve forgotten.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
